import { Injectable } from "@angular/core";
import { Observable, forkJoin, of } from "rxjs";
import { APIService } from "../api/api.service";
import { KPI } from "../api/models";
import { DatePipe } from "@angular/common";

@Injectable({
  providedIn: "root"
})
export class ComplianceService {
  constructor(private apiService: APIService, private datePipe: DatePipe) {}

  public createAssetCompliance(payload: KPI.AssetComplianceCreatePayload): Observable<KPI.AssetCompliance> {
    return this.apiService.create_asset_compliance(payload);
  }

  public updateAssetCompliance(id: number, payload: KPI.AssetComplianceUpdatePayload): Observable<KPI.AssetCompliance> {
    return this.apiService.patch_asset_compliance(id, payload);
  }

  public patchAssetCompliance(
    id: number,
    payload: Partial<KPI.AssetComplianceUpdatePayload>
  ): Observable<KPI.AssetCompliance> {
    return this.apiService.patch_asset_compliance(id, payload);
  }

  public mapFormToPayload(formValue: any, assetId: number): KPI.AssetComplianceCreatePayload {
    return {
      asset: assetId,
      control_required: formValue.regulatory_control === "" 
        ? null 
        : formValue.regulatory_control === "true",
      control_frequency: formValue.control_frequency || "",
      control_description: formValue.description || "",
      last_control_date: formValue.last_control_date
        ? this.datePipe.transform(formValue.last_control_date, "yyyy-MM-dd")
        : null,
      environmental_compliance: formValue.environmental_compliance === "" 
        ? null 
        : formValue.environmental_compliance === "true",
      compliance_type: "environmental", 
      certification_date: formValue.last_control_date
        ? this.datePipe.transform(formValue.last_control_date, "yyyy-MM-dd")
        : null,
      expiry_date: null,  
      certificate_number: "", 
      issuing_authority: "", 
      status: "active" 
    };
  }

  public mapFormToUpdatePayload(formValue: any, assetId: number): KPI.AssetComplianceUpdatePayload {
    return this.mapFormToPayload(formValue, assetId);
  }

  public bulkCreateReservations(
    payload: KPI.ReservationsBulkCreatePayload
  ): Observable<KPI.ReservationsBulkCreateResponse> {
    return this.apiService.bulk_create_reservations(payload);
  }

  public updateReservation(id: number, payload: KPI.ReservationUpdatePayload): Observable<KPI.Reservation> {
    return this.apiService.patch_reservation(id, payload);
  }

  public deleteReservation(id: number): Observable<void> {
    return this.apiService.delete_reservation(id);
  }

  public bulkUpdateReservations(
    reservations: Array<{ id: number; data: KPI.ReservationUpdatePayload }>
  ): Observable<KPI.Reservation[]> {
    if (reservations.length === 0) {
      return of([]);
    }

    const updateRequests = reservations.map(reservation => this.updateReservation(reservation.id, reservation.data));

    return forkJoin(updateRequests);
  }

  public bulkDeleteReservations(reservationIds: number[]): Observable<void[]> {
    if (reservationIds.length === 0) {
      return of([]);
    }

    const deleteRequests = reservationIds.map(id => this.deleteReservation(id));
    return forkJoin(deleteRequests);
  }

  public mapReservationsToPayload(reserves: any[]): KPI.ReservationCreatePayload[] {
    return reserves.map(reserve => ({
      label: reserve.libelle || "",
      issue_date: reserve.date_emission ? this.datePipe.transform(reserve.date_emission, "yyyy-MM-dd") : null,
      status: reserve.stade || null,
      criticality: reserve.criticite || null,
      responsible: reserve.responsable || null,
      closing_date: reserve.date_cloture ? this.datePipe.transform(reserve.date_cloture, "yyyy-MM-dd") : null,
      comment: reserve.commentaire || null,
      source: reserve.source || null,
    }));
  }

  public mapReservationsToUpdatePayload(reserves: any[]): Array<{ id: number; data: KPI.ReservationUpdatePayload }> {
    return reserves
      .filter(reserve => reserve.id) 
      .map(reserve => ({
        id: reserve.id,
        data: {
          label: reserve.libelle || "",
          issue_date: reserve.date_emission ? this.datePipe.transform(reserve.date_emission, "yyyy-MM-dd") : null,
          status: reserve.stade || null,
          criticality: reserve.criticite || null,
          responsible: reserve.responsable || null,
          closing_date: reserve.date_cloture ? this.datePipe.transform(reserve.date_cloture, "yyyy-MM-dd") : null,
          comment: reserve.commentaire || null,
          source: reserve.source || null,
        } as KPI.ReservationUpdatePayload
      }));
  }

  public listControlOfficeReports(params?: any): Observable<KPI.ControlOfficeReport[]> {
    return this.apiService.list_control_office_reports(params);
  }

  public getControlOfficeReport(id: number): Observable<KPI.ControlOfficeReport> {
    return this.apiService.get_control_office_report(id);
  }

  public createControlOfficeReport(payload: KPI.ControlOfficeReportCreatePayload): Observable<KPI.ControlOfficeReport> {
    return this.apiService.create_control_office_report(payload);
  }

  public updateControlOfficeReport(id: number, payload: KPI.ControlOfficeReportUpdatePayload): Observable<KPI.ControlOfficeReport> {
    return this.apiService.update_control_office_report(id, payload);
  }

  public patchControlOfficeReport(id: number, payload: Partial<KPI.ControlOfficeReportUpdatePayload>): Observable<KPI.ControlOfficeReport> {
    return this.apiService.patch_control_office_report(id, payload);
  }

  public deleteControlOfficeReport(id: number): Observable<void> {
    return this.apiService.delete_control_office_report(id);
  }

  public getReportReservations(id: number, params?: any): Observable<KPI.Reservation[]> {
    return this.apiService.get_report_reservations(id, params);
  }

  public getReportReservationsStats(id: number): Observable<KPI.ReportReservationsStats> {
    return this.apiService.get_report_reservations_stats(id);
  }
}
